import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicMilestones, FrontendMilestone } from '@/services/milestones.api';

export default function Timeline() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [milestones, setMilestones] = useState<FrontendMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const isZh = i18n.language === 'zh';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Fetch milestones from API
  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const response = await getPublicMilestones();
        // Sort by sortOrder, then by year
        const sorted = [...response].sort((a, b) => {
          if (a.sortOrder !== b.sortOrder) {
            return a.sortOrder - b.sortOrder;
          }
          return parseInt(a.year) - parseInt(b.year);
        });
        setMilestones(sorted);
      } catch (error) {
        console.error('Failed to fetch milestones:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMilestones();
  }, []);

  return (
    <section className="py-20 md:py-24 bg-[#F7F9FC]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs font-semibold mb-4 tracking-widest uppercase">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-history-line" />
            </div>
            {t('aboutPage.timeline.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540]">
            {t('aboutPage.timeline.title')}
          </h2>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2C7DA0] border-t-transparent"></div>
          </div>
        )}

        {/* Timeline */}
        {!loading && (
          <div className="relative">
            {/* Center line (desktop) */}
            <div className="hidden md:block absolute left-1/2 -translate-x-0.5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#2C7DA0]/40 via-[#2C7DA0]/60 to-[#2C7DA0]/20" />

            <div className="space-y-8 md:space-y-0">
              {milestones.map((milestone, idx) => {
              const isLeft = idx % 2 === 0;
              return (
                <div
                  key={milestone.id}
                  className={`relative flex flex-col md:flex-row ${isLeft ? '' : 'md:flex-row-reverse'} items-start md:items-center gap-4 md:gap-0 transition-all duration-700 delay-${Math.min(idx * 100, 500)} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                  {/* Content card */}
                  <div className={`md:w-[calc(50%-3rem)] ${isLeft ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                    <div className="bg-white rounded-2xl p-6 hover:-translate-y-0.5 transition-transform duration-200 border border-[#0A2540]/5">
                      <div className={`flex items-center gap-3 mb-3 ${isLeft ? 'md:flex-row-reverse' : ''}`}>
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0A2540] text-white shrink-0">
                          <i className={`${milestone.icon} text-base`} />
                        </div>
                        <span className="text-2xl font-bold text-[#2C7DA0]">{milestone.year}</span>
                      </div>
                      <h3 className="font-bold text-[#0A2540] text-base mb-2">
                        {isZh ? milestone.title : milestone.titleEn}
                      </h3>
                      <p className="text-[#0A2540]/60 text-sm leading-relaxed">
                        {isZh ? milestone.description : milestone.descriptionEn}
                      </p>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-[#2C7DA0] border-4 border-[#F7F9FC] items-center justify-center z-10" />

                  {/* Spacer */}
                  <div className="md:w-[calc(50%-3rem)]" />
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
